// lib/leasing/sourcingStrategyParams.ts
//
// The asset sourcing brief, merged.
//
// CONFIGURE_PROPERTY_SOURCING_STRATEGY writes six filters into one JSON column, and every one of
// them has an edge the caller gets wrong: [] means "stop filtering" and must DELETE rather than
// store an empty list the launch route would read as "match nothing"; 0 means "no bound" on the
// surfaces; omitted means "leave it alone" and must never be confused with "clear it"; and a model
// writes `"true"` as readily as `true`, which a typeof gate drops silently - no error, no write,
// and an agent that reports the change it believes it made.
//
// Pulled out of the handler because that file cannot be imported in jest (it drags the whole app
// graph in through an ESM dependency), so the rules could only ever be grepped for, never run.

import { asFormats, FORMATS } from '#lib/leasing/formatFit';

/** The stored shape. Kept loose on purpose: the launch route reads it and owns the contract. */
export type SourcingCategoryFilters = Record<string, unknown>;

export interface SourcingStrategyInput {
  minimumFitScore?: unknown;
  acceptedCnccActivities?: unknown;
  surfaceMinSqm?: unknown;
  surfaceMaxSqm?: unknown;
  acceptedFormats?: unknown;
  ignoreReferenceShortlist?: unknown;
}

/**
 * A switch as the caller meant it. `undefined` = not mentioned, leave it alone. Anything present
 * but unreadable is an error rather than a silent no-op, because silence here reads as success.
 */
export const readFlag = (value: unknown, name: string): boolean | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'boolean') return value;
  const text = String(value).trim().toLowerCase();
  if (['true', 'yes', 'on', '1'].includes(text)) return true;
  if (['false', 'no', 'off', '0'].includes(text)) return false;
  throw new Error(`"${name}" must be true or false.`);
};

/** m2, where 0 (or null) means "no bound" and clears the key. undefined leaves it alone. */
const readSurface = (value: unknown, name: string): number | null => {
  if (value === undefined) return null;
  if (value === null) return 0;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    throw new Error(`"${name}" must be a positive number of square metres, or 0 for no bound.`);
  }
  return Math.round(parsed);
};

/**
 * Merge a partial brief into the filters already stored on the property. Only the keys named in
 * `input` can change; everything else survives, because these six share one column and a partial
 * update used to be able to wipe a sibling.
 */
export const mergeSourcingCategoryFilters = (
  current: SourcingCategoryFilters | null | undefined,
  input: SourcingStrategyInput,
): SourcingCategoryFilters => {
  const next: SourcingCategoryFilters = { ...(current ?? {}) };

  if (input.minimumFitScore !== undefined && input.minimumFitScore !== null) {
    const parsed = Number(input.minimumFitScore);
    if (!Number.isFinite(parsed)) {
      throw new Error('"minimumFitScore" must be a number between 0 and 100.');
    }
    next.portfolioFitMinimumScore = Math.max(0, Math.min(100, Math.round(parsed)));
  }

  if (input.acceptedCnccActivities !== undefined) {
    if (!Array.isArray(input.acceptedCnccActivities)) {
      throw new Error('"acceptedCnccActivities" must be an array.');
    }
    const activities = Array.from(
      new Set(
        input.acceptedCnccActivities
          .map((value: unknown) => (typeof value === 'string' ? value.trim() : ''))
          .filter((value: string) => value.length > 0),
      ),
    );
    if (activities.length > 0) next.acceptedCnccActivities = activities;
    else delete next.acceptedCnccActivities;
  }

  const surfaceMin = readSurface(input.surfaceMinSqm, 'surfaceMinSqm');
  const surfaceMax = readSurface(input.surfaceMaxSqm, 'surfaceMaxSqm');
  if (surfaceMin !== null && surfaceMax !== null && surfaceMin > 0 && surfaceMax > 0 && surfaceMin > surfaceMax) {
    throw new Error('"surfaceMinSqm" cannot be greater than "surfaceMaxSqm".');
  }
  if (surfaceMin !== null) {
    if (surfaceMin > 0) next.surfaceMinSqm = surfaceMin;
    else delete next.surfaceMinSqm;
  }
  if (surfaceMax !== null) {
    if (surfaceMax > 0) next.surfaceMaxSqm = surfaceMax;
    else delete next.surfaceMaxSqm;
  }

  if (input.acceptedFormats !== undefined && input.acceptedFormats !== null) {
    if (!Array.isArray(input.acceptedFormats)) throw new Error('"acceptedFormats" must be a list.');
    const formats = asFormats(input.acceptedFormats.map((f) => String(f).trim().toUpperCase()));
    if (formats.length !== input.acceptedFormats.length) throw new Error(`"acceptedFormats" takes only ${FORMATS.join(', ')}.`);
    if (formats.length > 0) next.acceptedFormats = formats;
    else delete next.acceptedFormats;
  }

  const ignoreReferenceShortlist = readFlag(input.ignoreReferenceShortlist, 'ignoreReferenceShortlist');
  if (ignoreReferenceShortlist !== undefined) {
    if (ignoreReferenceShortlist) next.ignoreReferenceShortlist = true;
    else delete next.ignoreReferenceShortlist;
  }

  return next;
};
